import { useEffect, useRef, useState } from 'react';
import { Range, RangeKeyDict } from 'react-date-range';

import RadioButton from '@components/radio-button/RadioButton';
import { IdLabelValue } from '@types';
import useOnClickOutside from '@utils/hooks';

import Button from '../button/Button';
import Checkbox from '../checkbox/Checkbox';
import Icon from '../icon/Icon';
import CalendarModal from './CalendarModal';
import {
  FieldOption,
  FieldOptionObj,
  MultiFieldFilterProps,
  SelectedFilterValues
} from './types';

const getInitialField = (fieldOptions: FieldOptionObj) => {
  const allFields = Object.keys(fieldOptions);
  return allFields[0];
};

const MultiFieldFilter = (props: MultiFieldFilterProps) => {
  const {
    fieldOptions,
    handleApplyFilter,
    buttonClass,
    selectedFilters,
    defaultFilters,
    type = 'multi',
    customFields,
    setCustomFields,
    modalCustomStyle
  } = props;

  const [isOpen, setOpen] = useState<boolean>(false);
  const [selectedField, setSelectedField] = useState<string>(
    getInitialField(fieldOptions)
  );
  const [selectedDate, setSelectedDates] = useState<Range>({
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection'
  });

  const initalState =
    (Object.keys(selectedFilters).length && selectedFilters) || {};

  const [selectedItems, setSelectedItems] =
    useState<SelectedFilterValues>(initalState); // { status: ['pending', 'accepted'], role: ['admin'] }

  const countRef = useRef(0);
  const ref = useRef(null);

  useEffect(() => {
    countRef.current = Object.values(selectedFilters).flat().length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const onSelectFilterField = (field: string) => {
    setSelectedField(field);
  };

  const onCloseFilter = () => {
    setOpen(false);
    setSelectedField(getInitialField(fieldOptions));
    setSelectedItems({});
  };

  const onOpenFilter = () => {
    setOpen(true);
    setSelectedItems(initalState);
  };

  const toggleFilter = () => {
    if (isOpen) {
      onCloseFilter();
    } else {
      onOpenFilter();
    }
  };

  useOnClickOutside(ref, (e) => {
    e.stopPropagation();
    onCloseFilter();
    setOpen(false);
  });

  const onClearAllFilters = () => {
    if (type === 'single') {
      handleApplyFilter(defaultFilters as SelectedFilterValues);
    } else {
      handleApplyFilter({});
    }
    setSelectedDates({
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    });
    if (setCustomFields) {
      setCustomFields([]);
    }
    toggleFilter();
  };

  const onApplyFilters = () => {
    let updatedCustomFields = customFields;

    if (setCustomFields) {
      if (customFields) {
        const existingCustomField = customFields.find(
          (field) => field.key === selectedField
        );
        if (existingCustomField) {
          updatedCustomFields = customFields.map((field) =>
            field.key === selectedField
              ? { ...field, value: selectedDate }
              : field
          );
        } else {
          updatedCustomFields = [
            ...customFields,
            {
              key: selectedField,
              value: {
                startDate: selectedDate.startDate?.toISOString() || '',
                endDate: selectedDate.endDate?.toISOString() || ''
              }
            }
          ];
        }
      } else {
        updatedCustomFields = [
          {
            key: selectedField,
            value: {
              startDate: selectedDate.startDate?.toISOString() || '',
              endDate: selectedDate.endDate?.toISOString() || ''
            }
          }
        ];
      }

      // Update the state with the new custom fields
      setCustomFields(updatedCustomFields);
    }

    // Pass both filters and the updated custom fields to the parent
    handleApplyFilter(selectedItems, updatedCustomFields);
    toggleFilter();
  };

  // Modified method to check if current field should use radio buttons
  const isCurrentFieldSingleSelect = () => {
    const currentFieldOptions = fieldOptions[selectedField];
    return currentFieldOptions?.field === 'dateRange' || type === 'single';
  };

  const onSelectFilterItem = (item: IdLabelValue, isChecked: boolean) => {
    const isSingleSelect = isCurrentFieldSingleSelect();

    if (isSingleSelect) {
      // For single select (like dateRange), replace the selection
      setSelectedItems({
        ...selectedItems,
        [selectedField]: [item.value]
      });
    } else {
      // For multi-select, add/remove from array
      let updatedItems;
      const currentItems = selectedItems[selectedField]
        ? [...selectedItems[selectedField]]
        : [];
      if (isChecked) {
        updatedItems = [...currentItems, item.value];
      } else {
        updatedItems = currentItems.filter((oldItem) => oldItem !== item.value);
      }
      setSelectedItems({
        ...selectedItems,
        [selectedField]: updatedItems
      });
    }
  };

  const onSelectAll = (isChecked: boolean) => {
    let updatedItems: string[];
    if (isChecked) {
      const allOptions = fieldOptions[selectedField]?.options;
      updatedItems = allOptions.map((option) => option.value);
    } else {
      updatedItems = [];
    }
    setSelectedItems({
      ...selectedItems,
      [selectedField]: updatedItems
    });
  };

  // Todo: Move this to prev field
  const handleDateChange = (dateObject: RangeKeyDict) => {
    const { selection } = dateObject;
    setSelectedDates(selection);
  };

  const renderFieldCard = (item: FieldOption) => {
    const isSelectedField = selectedField === item.field;
    const countOfSelectedItemsPerField = selectedItems[item.field]?.length || 0;

    return (
      <div
        className={`flex cursor-pointer items-center justify-between rounded-lg p-4 text-sm text-content ${
          isSelectedField ? 'bg-selectionPrimary' : ''
        }`}
        role="presentation"
        onClick={() => onSelectFilterField(item.field)}
        key={item.id}
      >
        {item.label}
        {type === 'multi' && (
          <div
            className={`flex size-6 items-center justify-center rounded-xl bg-primary text-sm text-white ${
              countOfSelectedItemsPerField ? 'visible' : 'invisible'
            }`}
          >
            {countOfSelectedItemsPerField}
          </div>
        )}
      </div>
    );
  };

  const renderOptionsCard = (item: IdLabelValue) => {
    const isSingleSelect = isCurrentFieldSingleSelect();

    if (isSingleSelect) {
      return (
        <div key={item.id}>
          <RadioButton
            isSelected={selectedItems[selectedField]?.includes(item.value)}
            onChange={(e) => onSelectFilterItem(item, e.target.checked)}
            label={item.label}
            value={item.value}
            iconStyle="!size-5"
          />
          {selectedItems[selectedField]?.includes(item.value) &&
            item?.type &&
            item?.type === 'date' && (
              <CalendarModal
                handleDateChange={handleDateChange}
                selectedDate={selectedDate}
              />
            )}
        </div>
      );
    }

    return (
      <div key={item.id}>
        <Checkbox
          handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onSelectFilterItem(item, e.target.checked)
          }
          label={item.label}
          isChecked={selectedItems[selectedField]?.includes(item.value)}
          labelStyle="!text-content leading-6"
        />
      </div>
    );
  };

  // const renderSingleSelectOptionsCard = (item: IdLabelValue) => (
  //   <div key={item.id}>
  //     <RadioButton
  //       isSelected={selectedItems[selectedField]?.includes(item.value)}
  //       onChange={(e) => onSelectFilterItem(item, e.target.checked)}
  //       label={item.label}
  //       value={item.value}
  //       iconStyle="!size-5"
  //     />
  //     {selectedItems[selectedField]?.includes(item.value) &&
  //       item?.type &&
  //       item?.type === 'date' && (
  //         <CalendarModal
  //           handleDateChange={handleDateChange}
  //           selectedDate={selectedDate}
  //         />
  //       )}
  //   </div>
  // );

  const renderSelectAll = () => {
    const isSingleSelect = isCurrentFieldSingleSelect();

    // Don't show "Select All" for single-select fields
    if (isSingleSelect) {
      return null;
    }

    const optionsLength = fieldOptions[selectedField]?.options?.length;
    const selectedFieldItemsLength = selectedItems[selectedField]?.length;
    return (
      <Checkbox
        handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onSelectAll(e.target.checked)
        }
        label="Select All"
        isChecked={selectedFieldItemsLength === optionsLength}
        labelStyle="!text-content leading-6"
        checkboxStyle="mr-3"
      />
    );
  };

  const renderCountIndicator = () => {
    const totalSelectedItemsCount = countRef.current;
    return (
      <div
        className={`absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-xl bg-primary text-xs text-theme ${
          totalSelectedItemsCount ? 'visible' : 'invisible'
        }`}
      >
        {totalSelectedItemsCount}
      </div>
    );
  };

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Button
          variant="tertiary"
          handleClick={toggleFilter}
          size="medium"
          label="Filter"
          className={`ml-auto w-24 justify-between ${buttonClass}`}
          leftIconName="filter"
        />
        {type === 'multi' && renderCountIndicator()}
      </div>
      {isOpen && (
        <div
          className={`absolute right-0 z-50 mt-1 flex min-w-623 origin-top-right flex-col justify-between rounded-lg bg-theme p-6 shadow-multiFieldFilterShadow ${modalCustomStyle}`}
        >
          <div className="mb-4 flex items-center justify-between text-base font-medium">
            <div>Filters</div>
            <div
              className="ml-auto size-4 cursor-pointer"
              role="presentation"
              onClick={toggleFilter}
            >
              <Icon name="close" size="none" className="size-4" />
            </div>
          </div>

          <div className="flex size-full overflow-y-auto">
            <div className="flex w-1/2 flex-col pr-3">
              <div className="flex flex-col">
                {Object.values(fieldOptions).map((item) =>
                  renderFieldCard(item)
                )}
              </div>
            </div>
            <div className="flex w-1/2 flex-col gap-4 border-l border-l-greyStroke p-3">
              <div className="flex items-center justify-between text-base font-medium leading-6 text-content">
                {fieldOptions[selectedField]?.label}
                {renderSelectAll()}
              </div>
              <div className="grid gap-3">
                {fieldOptions[selectedField]?.options?.map((item) =>
                  renderOptionsCard(item)
                )}
              </div>
            </div>
          </div>
          <div className="mt-6 flex items-center justify-end">
            <div className="grid w-1/2 grid-cols-2 gap-3">
              <Button
                label="Clear"
                variant="secondary"
                handleClick={onClearAllFilters}
              />
              <Button label="Apply Filter" handleClick={onApplyFilters} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiFieldFilter;
